import { ArrowDropDown, CopyAll, Delete } from "@mui/icons-material";
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  switchToViewAtom,
  deleteViewAtom,
  duplicateViewAtom,
} from "datasheets/model/atoms/WorkbookActionAtoms";
import { useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";
import { Button } from "react-bootstrap";
import {
  selectedViewAtom,
  viewOrderAtom,
  useViewNameForKeyAtom,
} from "datasheets/model/atoms/WorkbookFocusAtoms";
import { EditableTextField } from "datasheets/components/EditableTextField";

/**
 * Component to display editable view name on a tab
 *
 */
interface Props {
  viewId: string;
  index: number;
  isDragging: boolean;
  isDropTarget?: boolean;
}
export function EditableViewName({
  viewId,
  index,
  isDragging,
  isDropTarget,
}: Props) {
  const tabs = useAtomValue(viewOrderAtom);
  const selectedView = useAtomValue(selectedViewAtom);
  const switchToView = useSetAtom(switchToViewAtom);
  const [viewName, setViewName] = useViewNameForKeyAtom(viewId);
  const placeholderWhileDragging = <Divider orientation="vertical"></Divider>;
  const tab = (
    <EditableTextField
      editorRole="sheetname-editor"
      displayText={viewName}
      setDisplayText={setViewName}
      doubleClick={true}
    >
      <Button
        role="sheetname-display"
        variant={isDragging ? "outlined" : "light"}
        onClick={() => switchToView(index)}
        active={selectedView === viewId}
        key={viewId}
        style={isDropTarget ? { opacity: "0.5" } : {}}
      >
        {viewName}
      </Button>
      <TabContextMenu viewId={tabs[index]} canDelete={tabs.length > 1} />
    </EditableTextField>
  );
  const content = isDragging ? placeholderWhileDragging : tab;
  return <>{content}</>;
}

function TabContextMenu({
  viewId,
  canDelete,
}: {
  viewId: string;
  canDelete: boolean;
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const deleteView = useSetAtom(deleteViewAtom);
  const duplicateView = useSetAtom(duplicateViewAtom);

  const handleMenuOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (actionFn: () => void) => {
    return () => {
      handleMenuClose();
      actionFn();
    };
  };

  function deleteViewHandler() {
    //TODO: dont delete dataset if there are still references to it
    deleteView({ viewId: viewId });
  }

  function duplicateViewHandler() {
    //TODO: provide option to refer to same underlying dataset
    duplicateView({ viewId: viewId });
  }

  return (
    <>
      <ArrowDropDown onClick={handleMenuOpen} />
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleClick(duplicateViewHandler)}>
          <ListItemIcon>
            <CopyAll fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate Tab</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleClick(deleteViewHandler)} hidden={!canDelete}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Tab</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
